import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  Image,
  StyleSheet,
  Svg,
  Rect,
} from "@react-pdf/renderer";
import QRCode from "qrcode";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import LogoImg from "../../../assets/images/logos/trip-logo.png";
import { HOTEL_PDF_FONT_FAMILY } from "../Hotel/hotelPdfFont";
import { formatDate } from "../../../helpers/dateHelper";

dayjs.extend(customParseFormat);

const NAVY = "#16324F";
const RED = "#C81E1E";
const INK = "#1C2B39";
const MUTED = "#5E6A75";
const LINE = "#E4EAF1";
const SOFT = "#F5F8FB";
const WHITE = "#FFFFFF";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: HOTEL_PDF_FONT_FAMILY,
    backgroundColor: WHITE,
    color: INK,
  },
  accent: { height: 6, backgroundColor: RED, marginBottom: 16 },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  brand: { width: "38%", paddingRight: 8 },
  centerTitle: { width: "24%", alignItems: "center" },
  logo: { width: 78, height: 42, objectFit: "contain", marginBottom: 8 },
  agencyName: { fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 3 },
  address: { fontSize: 8, color: MUTED, lineHeight: 1.4 },
  docBlock: { width: "38%", alignItems: "flex-end" },
  docKicker: {
    fontSize: 9,
    color: RED,
    fontWeight: 700,
    marginBottom: 4,
    textAlign: "right",
  },
  docTitle: { fontSize: 16, fontWeight: 700, color: NAVY, textAlign: "center" },
  docNo: { fontSize: 9, color: INK, marginBottom: 2 },
  docDate: { fontSize: 8, color: MUTED },
  cardRow: { flexDirection: "row", marginBottom: 12 },
  card: { width: "49%", backgroundColor: SOFT, borderRadius: 6, padding: 10 },
  cardGap: { width: "2%" },
  cardLabel: {
    fontSize: 7,
    letterSpacing: 0.8,
    color: RED,
    fontWeight: 700,
    marginBottom: 4,
  },
  cardTitle: { fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 3 },
  cardText: { fontSize: 8, color: MUTED, marginBottom: 2 },
  metaLine: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  metaKey: { fontSize: 8, color: MUTED, width: "46%" },
  metaVal: {
    fontSize: 8,
    fontWeight: 700,
    color: INK,
    width: "54%",
    textAlign: "right",
  },
  stay: {
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 6,
    padding: 10,
    marginBottom: 12,
  },
  stayName: { fontSize: 12, fontWeight: 700, color: NAVY, marginBottom: 2 },
  stayCity: { fontSize: 8, color: MUTED, marginBottom: 8 },
  stayFacts: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: LINE,
    paddingTop: 8,
  },
  fact: { width: "20%" },
  factLabel: { fontSize: 7, color: MUTED, marginBottom: 2 },
  factValue: { fontSize: 9, fontWeight: 700, color: INK },
  legLabel: {
    fontSize: 8,
    fontWeight: 700,
    color: RED,
    marginBottom: 6,
  },
  table: {
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 4,
    overflow: "hidden",
    marginBottom: 8,
  },
  tableHead: { flexDirection: "row", backgroundColor: NAVY },
  tableRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: LINE },
  tableRowAlt: { backgroundColor: SOFT },
  th: { fontSize: 7, fontWeight: 700, color: WHITE, padding: 5 },
  td: { fontSize: 7.5, color: INK, padding: 5 },
  totalsWrap: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginTop: 8,
  },
  qrBox: { width: "46%", alignItems: "flex-end" },
  totals: {
    width: "46%",
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 6,
    overflow: "hidden",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "7 10",
    borderBottomWidth: 1,
    borderBottomColor: LINE,
  },
  totalLabel: { fontSize: 8, color: MUTED },
  totalValue: { fontSize: 8, color: INK },
  grandRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: "8 10",
    backgroundColor: NAVY,
  },
  grandLabel: { fontSize: 9, fontWeight: 700, color: WHITE },
  grandValue: { fontSize: 9, fontWeight: 700, color: WHITE },
  footer: {
    marginTop: 16,
    borderWidth: 1,
    borderColor: LINE,
    borderRadius: 6,
    padding: 10,
  },
  footerHead: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  footerTitle: { fontSize: 14, fontWeight: 700, color: "#111111" },
  footerNote: { fontSize: 7, color: MUTED },
  bankTable: { borderWidth: 1, borderColor: "#111111" },
  bankHead: { flexDirection: "row" },
  bankBody: {
    flexDirection: "row",
    borderTopWidth: 1,
    borderTopColor: "#111111",
  },
  bankTh: {
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#111111",
  },
  bankThText: {
    fontSize: 8,
    fontWeight: 700,
    color: "#111111",
  },
  bankTd: {
    padding: 4,
    borderRightWidth: 1,
    borderRightColor: "#111111",
    overflow: "hidden",
  },
  bankTdText: {
    fontSize: 8,
    color: "#111111",
  },
  bankCellLast: { borderRightWidth: 0 },
  termsTitle: {
    fontSize: 9,
    fontWeight: 700,
    color: RED,
    marginTop: 10,
    marginBottom: 4,
  },
  termLine: { fontSize: 7, color: INK, marginBottom: 2, lineHeight: 1.3 },
  note: { marginTop: 12, fontSize: 8, color: MUTED, textAlign: "center" },
});

const breakLongText = (value) =>
  String(value ?? "").replace(/[^\s]{12,}/g, (token) =>
    token.match(/.{1,12}/g).join("\u200b"),
  );

const joinParts = (parts) =>
  parts
    .map((part) => String(part ?? "").trim())
    .filter(Boolean)
    .join(", ");

const formatInvoiceDate = (value) => {
  const formatted = formatDate(value, "DD MMM YYYY");
  if (formatted) return formatted;
  if (value == null || value === "") return "—";
  const raw = String(value).trim();
  const parsed = dayjs(raw.includes("T") ? raw : raw.replace(" ", "T"));
  return parsed.isValid() ? parsed.format("DD MMM YYYY") : "—";
};

const money = (amount) => `INR ${Number(amount || 0).toFixed(2)}`;

const InvoiceQr = ({ value }) => {
  let qr;
  try {
    qr = QRCode.create(String(value || "Invoice"), { errorCorrectionLevel: "M" });
  } catch {
    return null;
  }
  const count = qr.modules.size;
  const quiet = 2;
  const total = count + quiet * 2;
  const cells = [];
  for (let y = 0; y < count; y += 1) {
    for (let x = 0; x < count; x += 1) {
      if (qr.modules.get(x, y)) {
        cells.push(
          <Rect
            key={`${x}-${y}`}
            x={x + quiet}
            y={y + quiet}
            width={1}
            height={1}
            fill={NAVY}
          />,
        );
      }
    }
  }
  return (
    <Svg width={110} height={110} viewBox={`0 0 ${total} ${total}`}>
      <Rect x={0} y={0} width={total} height={total} fill={WHITE} />
      {cells}
    </Svg>
  );
};

const passengerName = (pax) =>
  [pax?.title, pax?.firstName, pax?.lastName].filter(Boolean).join(" ") || "—";

const sectorText = (first, last) =>
  [first?.origin, last?.destination || first?.destination]
    .filter(Boolean)
    .join(" - ") || "—";

const FlightInvoiceDoc = ({ invoiceData, logo, type }) => {
  const isCreditNote = type === "credit_note";
  const gst = invoiceData?.GSTDetails?.[0] ?? {};
  const onwardSegments = Array.isArray(invoiceData?.oneWaySegment)
    ? invoiceData.oneWaySegment
    : [];
  const returnSegments = Array.isArray(invoiceData?.returnSegment)
    ? invoiceData.returnSegment
    : [];
  const onwardFirst = onwardSegments[0];
  const onwardLast = onwardSegments[onwardSegments.length - 1];
  const returnFirst = returnSegments[0];
  const returnLast = returnSegments[returnSegments.length - 1];
  const isRoundTrip = invoiceData?.tripType === "roundTrip";
  const pnrText = String(invoiceData?.pnr ?? "");
  const pnrParts = pnrText.includes("~") ? pnrText.split("~") : [pnrText];
  const onwardPnr = isRoundTrip ? pnrParts[0] : pnrText;
  const returnPnr = pnrParts[1] || pnrText;
  const commission = Number(invoiceData?.commission ?? 0);
  const tds = (commission * 5) / 100;
  const passengers = Array.isArray(invoiceData?.passengers)
    ? invoiceData.passengers
    : [];
  const bookingDate = formatInvoiceDate(
    invoiceData?.CreatedOn || invoiceData?.bookingDate,
  );
  const onwardDate = formatInvoiceDate(onwardFirst?.departureDateTime);
  const returnDate = formatInvoiceDate(returnFirst?.departureDateTime);
  const documentNo = isCreditNote
    ? invoiceData?.creditNoteNo
    : invoiceData?.invoiceNo;
  const agencyName = String(
    invoiceData?.agentDetails?.AgencyName ??
      invoiceData?.agentDetails?.CompanyName ??
      "",
  ).trim();
  const address = invoiceData?.agentDetails
    ? joinParts([
        invoiceData.agentDetails.Address1,
        invoiceData.agentDetails.Address2,
        invoiceData.agentDetails.City,
        invoiceData.agentDetails.State,
      ])
    : String(invoiceData?.adminDetails?.[0]?.Address ?? "").trim();
  const contactPhone = String(invoiceData?.agentDetails?.Phone ?? "").trim();
  const contactEmail = String(
    invoiceData?.agentDetails?.Email ?? invoiceData?.guestEmaiId ?? "",
  ).trim();
  const panNumber = String(invoiceData?.agentDetails?.PAN ?? "").trim();
  const guestName = String(invoiceData?.guestName ?? "").trim() || "—";
  const guestEmail = String(invoiceData?.guestEmaiId ?? "").trim();
  const airline = String(invoiceData?.operator ?? "").trim() || "—";
  const logoSrc =
    logo && typeof Blob !== "undefined" && logo instanceof Blob
      ? logo
      : typeof logo === "string" && logo.startsWith("data:image/")
        ? logo
        : LogoImg;
  const columns = [
    { key: "pax", label: "Passenger", width: "28%" },
    { key: "date", label: "Travel Date", width: "18%" },
    { key: "flight", label: "Flight", width: "22%" },
    { key: "sector", label: "Sector", width: "18%" },
    { key: "pnr", label: "PNR", width: "14%" },
  ];
  const rows = passengers.flatMap((pax, index) => {
    const onward = {
      key: `on-${index}`,
      pax: passengerName(pax),
      date: onwardDate,
      flight: airline,
      sector: sectorText(onwardFirst, onwardLast),
      pnr: onwardPnr || "—",
    };
    if (!isRoundTrip) return [onward];
    return [
      onward,
      {
        key: `rt-${index}`,
        pax: passengerName(pax),
        date: returnDate,
        flight: airline,
        sector: sectorText(returnFirst, returnLast),
        pnr: returnPnr || "—",
      },
    ];
  });
  const qrValue = [
    `Invoice No: ${documentNo || "-"}`,
    `Booking Date: ${bookingDate}`,
    `Reference No: ${invoiceData?.referenceNumber || "-"}`,
    `Passenger: ${guestName}`,
    `Airline: ${airline}`,
    `Sector: ${sectorText(onwardFirst, onwardLast)}`,
    `Travel Date: ${onwardDate}`,
    isRoundTrip ? `Return: ${sectorText(returnFirst, returnLast)} ${returnDate}` : "",
    `PNR: ${pnrText || "-"}`,
    `Passengers: ${passengers.length || "-"}`,
    `Grand Total: ${money(invoiceData?.subTotal)}`,
  ]
    .filter(Boolean)
    .join("\n");
  const bankColumns = [
    { key: "bankName", label: "Bank Name", width: "18%" },
    { key: "accountName", label: "Account Name", width: "16%" },
    { key: "accountNo", label: "Acc No / IBAN No", width: "22%" },
    { key: "ifsc", label: "IFSC Code", width: "14%" },
    { key: "branch", label: "Branch", width: "16%" },
    { key: "country", label: "Country", width: "14%" },
  ];
  const banks = [
    {
      bankName: "HDFC Bank Ltd ( INDIA )",
      accountName: "SHOP YOUR TRIP PVT LTD",
      accountNo: "50200056477211",
      ifsc: "HDFC0003717",
      branch: "HDFC Bank Ltd",
      country: "India",
    },
    {
      bankName: "ICICI BANK LTD ( INDIA )",
      accountName: "ICICI BANK LTD",
      accountNo: "418005000835",
      ifsc: "ICIC0004180",
      branch: "ICICI BANK LTD",
      country: "India",
    },
    {
      bankName: "IDFC FIRST BANK ( INDIA )",
      accountName: "IDFC FIRST BANK",
      accountNo: "10093620851",
      ifsc: "IDFB0020137",
      branch: "Nehru Place",
      country: "India",
    },
    {
      bankName: "WESTPAC BANKING CORPORATION ( AUSTRALIA )",
      accountName: "SHOP YOUR TRIP PTY LTD",
      accountNo: "723441",
      ifsc: "032-778",
      branch: "CANBERRA",
      country: "Australia",
    },
    {
      bankName: "RAK BANK ( UAE )",
      accountName: "SYT TRAVEL & TOURISM LLC",
      accountNo: "0033146717001",
      ifsc: "NRAKAEAK",
      branch: "Deira - Riggat Al Buteen",
      country: "United Arab Emirates",
    },
    {
      bankName: "Paysera LT, UAB ( EUROPE )",
      accountName: "SASTA EUROPE",
      accountNo: "LT913500010017371289",
      ifsc: "EVIULT2VXXX",
      branch: "VILNIUS, Lithuania",
      country: "Lithuania",
    },
    {
      bankName: "BANK OF AMERICA ( USA )",
      accountName: "SASTA USA TOURS LLC",
      accountNo: "325200966946",
      ifsc: "BOFAUS3N",
      branch: "855 POLLASKY AVE, CLOVIS CA 93612",
      country: "United States",
    },
    {
      bankName: "REVOLUT BANK ( EUROPE )",
      accountName: "SASTA EUROPE UAB",
      accountNo: "LT883250012552750928",
      ifsc: "CHASDEFX",
      branch: "Revolut Bank UAB",
      country: "Lithuania",
    },
  ];

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.accent} />
        <View style={styles.header}>
          <View style={styles.brand}>
            <Image style={styles.logo} src={logoSrc} />
            {agencyName ? (
              <Text style={styles.agencyName}>{agencyName}</Text>
            ) : null}
            {address ? <Text style={styles.address}>{address}</Text> : null}
            {contactPhone ? (
              <Text style={styles.address}>Phone: {contactPhone}</Text>
            ) : null}
            {contactEmail ? (
              <Text style={styles.address}>Email: {contactEmail}</Text>
            ) : null}
            {panNumber ? (
              <Text style={styles.address}>PAN: {panNumber}</Text>
            ) : null}
          </View>
          <View style={styles.centerTitle}>
            <Text style={styles.docTitle}>
              {isCreditNote ? "Credit Note" : "Invoice"}
            </Text>
          </View>
          <View style={styles.docBlock}>
            <Text style={styles.docKicker}>Flight Booking Details</Text>
            <Text style={styles.docDate}>
              {isCreditNote ? "Credit Note No." : "Invoice No."}
            </Text>
            <Text style={styles.docNo}>{documentNo || "—"}</Text>
            <Text style={styles.docDate}>
              {isCreditNote ? "Credit Note Date" : "Invoice Date"}
            </Text>
            <Text style={styles.docNo}>{bookingDate}</Text>
          </View>
        </View>

        <View style={styles.cardRow}>
          <View style={styles.card}>
            <Text style={styles.cardLabel}>BILLED TO</Text>
            <Text style={styles.cardTitle}>{guestName}</Text>
            {guestEmail ? <Text style={styles.cardText}>{guestEmail}</Text> : null}
            {gst.GSTCompanyName ? (
              <Text style={styles.cardText}>{gst.GSTCompanyName}</Text>
            ) : null}
            <Text style={styles.cardText}>
              GST No: {gst.GSTNumber || "N/A"}
            </Text>
          </View>
          <View style={styles.cardGap} />
          <View style={styles.card}>
            <Text style={styles.cardLabel}>BOOKING</Text>
            <View style={styles.metaLine}>
              <Text style={styles.metaKey}>Reference No.</Text>
              <Text style={styles.metaVal}>
                {invoiceData?.referenceNumber || "—"}
              </Text>
            </View>
            <View style={styles.metaLine}>
              <Text style={styles.metaKey}>Booking Date</Text>
              <Text style={styles.metaVal}>{bookingDate}</Text>
            </View>
            <View style={styles.metaLine}>
              <Text style={styles.metaKey}>PNR</Text>
              <Text style={styles.metaVal}>{pnrText || "—"}</Text>
            </View>
          </View>
        </View>

        <View style={styles.stay}>
          <Text style={styles.legLabel}>
            {isRoundTrip ? "Onward" : "Flight"}
          </Text>
          <Text style={styles.stayName}>
            {sectorText(onwardFirst, onwardLast)}
          </Text>
          <Text style={styles.stayCity}>{airline}</Text>
          <View style={styles.stayFacts}>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Travel Date</Text>
              <Text style={styles.factValue}>{onwardDate}</Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>From</Text>
              <Text style={styles.factValue}>{onwardFirst?.origin || "—"}</Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>To</Text>
              <Text style={styles.factValue}>
                {onwardLast?.destination || onwardFirst?.destination || "—"}
              </Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>PNR</Text>
              <Text style={styles.factValue}>{onwardPnr || "—"}</Text>
            </View>
            <View style={styles.fact}>
              <Text style={styles.factLabel}>Passengers</Text>
              <Text style={styles.factValue}>{passengers.length || "—"}</Text>
            </View>
          </View>
          {isRoundTrip ? (
            <View style={{ marginTop: 10 }}>
              <Text style={styles.legLabel}>Return</Text>
              <Text style={styles.stayName}>
                {sectorText(returnFirst, returnLast)}
              </Text>
              <View style={styles.stayFacts}>
                <View style={styles.fact}>
                  <Text style={styles.factLabel}>Travel Date</Text>
                  <Text style={styles.factValue}>{returnDate}</Text>
                </View>
                <View style={styles.fact}>
                  <Text style={styles.factLabel}>From</Text>
                  <Text style={styles.factValue}>
                    {returnFirst?.origin || "—"}
                  </Text>
                </View>
                <View style={styles.fact}>
                  <Text style={styles.factLabel}>To</Text>
                  <Text style={styles.factValue}>
                    {returnLast?.destination || returnFirst?.destination || "—"}
                  </Text>
                </View>
                <View style={styles.fact}>
                  <Text style={styles.factLabel}>PNR</Text>
                  <Text style={styles.factValue}>{returnPnr || "—"}</Text>
                </View>
                <View style={styles.fact}>
                  <Text style={styles.factLabel}>Airline</Text>
                  <Text style={styles.factValue}>{airline}</Text>
                </View>
              </View>
            </View>
          ) : null}
        </View>

        <View style={styles.table}>
          <View style={styles.tableHead}>
            {columns.map((column) => (
              <Text key={column.key} style={{ ...styles.th, width: column.width }}>
                {column.label}
              </Text>
            ))}
          </View>
          {rows.map((row, index) => (
            <View
              key={row.key}
              style={
                index % 2 === 1
                  ? { ...styles.tableRow, ...styles.tableRowAlt }
                  : styles.tableRow
              }
            >
              {columns.map((column) => (
                <Text
                  key={column.key}
                  style={{ ...styles.td, width: column.width }}
                >
                  {String(row[column.key] ?? "—")}
                </Text>
              ))}
            </View>
          ))}
        </View>

        <View style={styles.totalsWrap}>
          <View style={styles.totals}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Sub Total</Text>
              <Text style={styles.totalValue}>
                {money(invoiceData?.subTotal)}
              </Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Tax</Text>
              <Text style={styles.totalValue}>{money(0)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Discount</Text>
              <Text style={styles.totalValue}>{commission.toFixed(2)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>TDS</Text>
              <Text style={styles.totalValue}>{tds.toFixed(2)}</Text>
            </View>
            {isCreditNote ? (
              <View style={styles.totalRow}>
                <Text style={styles.totalLabel}>Refunded Amount</Text>
                <Text style={styles.totalValue}>
                  {money(invoiceData?.RefundAmount)}
                </Text>
              </View>
            ) : null}
            <View style={styles.grandRow}>
              <Text style={styles.grandLabel}>Grand Total</Text>
              <Text style={styles.grandValue}>
                {money(invoiceData?.subTotal)}
              </Text>
            </View>
          </View>
          <View style={styles.qrBox}>
            <InvoiceQr value={qrValue} />
          </View>
        </View>

        <View style={styles.footer}>
          <View style={styles.footerHead}>
            <Text style={styles.footerTitle}>Bank Details(Current Account)</Text>
            <Text style={styles.footerNote}>E. & O.E.</Text>
          </View>
          <View style={styles.bankTable}>
            <View style={styles.bankHead} wrap={false}>
              {bankColumns.map((column, index) => (
                <View
                  key={column.key}
                  style={{
                    ...styles.bankTh,
                    width: column.width,
                    ...(index === bankColumns.length - 1
                      ? styles.bankCellLast
                      : {}),
                  }}
                >
                  <Text style={styles.bankThText}>{column.label}</Text>
                </View>
              ))}
            </View>
            {banks.map((bank) => (
              <View key={bank.accountNo} style={styles.bankBody} wrap={false}>
                {bankColumns.map((column, index) => (
                  <View
                    key={column.key}
                    style={{
                      ...styles.bankTd,
                      width: column.width,
                      ...(index === bankColumns.length - 1
                        ? styles.bankCellLast
                        : {}),
                    }}
                  >
                    <Text style={styles.bankTdText}>
                      {breakLongText(bank[column.key])}
                    </Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
          <Text style={styles.termsTitle}>Terms & Conditions</Text>
          <Text style={styles.termLine}>
            CASH: Payment to be made to the cashier and a printed official receipt must be obtained.
          </Text>
          <Text style={styles.termLine}>
            CHEQUE: All cheques or demand drafts must be crossed A/c Payee Only and drawn in favour of SHOP YOUR TRIP PRIVATE LIMITED.
          </Text>
          <Text style={styles.termLine}>
            LATE PAYMENT: Interest at 24% per annum will be charged on all outstanding bills after the due date.
          </Text>
          <Text style={styles.termLine}>
            VERY IMP: Kindly check all details carefully to avoid unnecessary complications.
          </Text>
        </View>

        <Text style={styles.note}>
          This is an electronically generated{" "}
          {isCreditNote ? "credit note" : "invoice"} and does not require a
          signature.
        </Text>
      </Page>
    </Document>
  );
};

export default FlightInvoiceDoc;
